import { SECOND_UNLOCK_GW } from '../../chipRules'

/**
 * The single source of truth for every rules string in the app.
 *
 * Two surfaces read this and neither holds copy of its own: the how-to-play screen and the
 * contextual ⓘ sheet. Keeping it here also means the copy is extractable for translation later
 * without touching a component.
 *
 * The budget in `content.test.ts` is the point, not a guideline. A section that won't fit is a
 * mechanic that is too complicated — raise the mechanic rather than the limit.
 */

export type RulesSectionId =
  | 'scoring'
  | 'banker'
  | 'chips'
  | 'deadlines'
  | 'intuition'
  | 'sharpest'
  | 'leagues'
  | 'rivals'

export type RulesVisual =
  | 'scoring-card'
  | 'banker-toggle'
  | 'chip-row'
  | 'deadline-clock'
  | 'intuition-tiers'
  | 'sharpest-week'
  | 'league-row'
  | 'rivals-pair'

export interface RulesSection {
  id: RulesSectionId
  /** Nav label + section heading. */
  title: string
  /** One sentence. The whole mechanic in a breath. */
  lede: string
  /** Optional. Edge cases only — not a restatement of the lede. */
  points?: string[]
  /** Demo component key. Every section must have one. */
  visual: RulesVisual
  /** ISO date. Bump when the mechanic itself changes, not when the wording is tidied. */
  updated: string
}

/**
 * Order is fixed and meaningful: scoring first, because that is what people check mid-gameweek.
 *
 * Every section reads standalone. Someone arriving at `banker` from the toggle has not read
 * `scoring`, so no section may refer to another by position or assume prior context.
 */
export const RULES_SECTIONS: RulesSection[] = [
  {
    id: 'scoring',
    title: 'Scoring',
    lede: 'A fixture pays 5 for the exact score, 3 for the right outcome, 1 for a close miss.',
    visual: 'scoring-card',
    updated: '2026-08-12',
  },
  {
    id: 'banker',
    title: 'Banker',
    lede: 'Pick one fixture each gameweek as your Banker and its points are doubled.',
    visual: 'banker-toggle',
    updated: '2026-08-12',
  },
  {
    id: 'chips',
    title: 'Chips',
    lede: 'Three chips boost a gameweek, and no two can run at once.',
    points: [
      `Two of each a season, second use unlocks at GW${SECOND_UNLOCK_GW}`,
      'Clean Sweep: miss one outcome and the gameweek scores 0',
    ],
    visual: 'chip-row',
    updated: '2026-08-12',
  },
  {
    id: 'deadlines',
    title: 'Deadlines',
    lede: 'Predictions lock 30 minutes before the first kickoff of the gameweek.',
    points: [
      'Unpredicted fixtures auto-fill 1–1 at the deadline',
      'An auto pick pays 2 for a draw, nothing else',
      'Miss a gameweek and you fall behind, not out',
    ],
    visual: 'deadline-clock',
    updated: '2026-08-12',
  },
  {
    id: 'intuition',
    title: 'Intuition Rating',
    lede: 'Intuition Rating is how often the outcomes you call match the real ones.',
    points: [
      'Only fixtures you picked yourself count',
      'Calibrating until you have three scored gameweeks',
      'Tiers run Rookie, Sharp, Analyst, Oracle',
    ],
    visual: 'intuition-tiers',
    updated: '2026-09-05',
  },
  {
    id: 'sharpest',
    title: 'Sharpest week',
    lede: 'Your sharpest week is the gameweek your picks read the football best.',
    points: [
      'Ranked on the football alone, so a chip never decides it',
      'The points shown are what you actually collected that week',
    ],
    visual: 'sharpest-week',
    updated: '2026-08-25',
  },
  {
    id: 'leagues',
    title: 'Mini-leagues',
    lede: 'Create a mini-league or join one with its code to rank against people you pick.',
    // One point rather than several: four points is over the
    // per-section budget in content.test.ts, and how you get *into* a league now beats a caption
    // for the visual below (which already shows the rank and its move) and the Global aside.
    points: [
      'Explore lists leagues you can join outright or ask to join',
      'Invite-only leagues stay unlisted, you need the code',
    ],
    visual: 'league-row',
    updated: '2026-08-12',
  },
  {
    id: 'rivals',
    title: 'Rivals',
    lede: 'Challenge a player to become rivals and every gameweek scores a win or a loss.',
    visual: 'rivals-pair',
    updated: '2026-08-12',
  },
]

export function rulesSection(id: RulesSectionId): RulesSection {
  const section = RULES_SECTIONS.find((s) => s.id === id)
  if (!section) throw new Error(`Unknown rules section: ${id}`)
  return section
}

/** The most recent `updated` across every section — the screen's "Last updated" footer. */
export function rulesLastUpdated(): string {
  return RULES_SECTIONS.reduce(
    (latest, section) => (section.updated > latest ? section.updated : latest),
    RULES_SECTIONS[0].updated
  )
}
